import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Buy-N-pick-N coffret gift picker. Single-pick mode (entitlement == 1)
 * renders plain clickable cards handled by handleSelect; stepper mode
 * (entitlement > 1) renders per-card +/- handled by handleIncrement /
 * handleDecrement. Both add/remove via properties[_bogo_routines_gift]
 * so the deployed cart-promo-discount Function recognizes and zeroes
 * the line.
 *
 * Also owns a background reconciler (module-level, below the class) that
 * keeps the cart's actual gift-line quantities in sync with entitlement
 * on every cart:update — ported from grid-2027's assets/cart-bogo-routines.js
 * (same trim/top-up rules), since the click handlers alone only ever ask
 * for a +/-1 change against a snapshot taken at click time and can't by
 * themselves catch e.g. a trigger coffret being removed entirely, or a
 * newly bought coffret's entitlement never getting spent.
 *
 * @extends {Component}
 */
class CartBogoRoutinesComponent extends Component {
  /** @type {boolean} */
  #isUpdating = false;

  /**
   * Bound to both on:click and on:keydown (single-pick mode cards use
   * role="button" tabindex="0", not a native <button>, so Enter/Space
   * activation has to be handled explicitly — same convention as
   * assets/facets.js's removeFilter()).
   *
   * @param {{ variantId: string }} data
   * @param {Event} event
   */
  handleSelect = async (data, event) => {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    if (!data?.variantId || this.#isUpdating) return;
    if (!acquireGiftLock()) return;
    this.#isUpdating = true;
    const card = event.currentTarget instanceof Element ? event.currentTarget.closest('[data-variant-id]') : null;
    this.#setCardBusy(card, true);
    try {
      await this.#syncPick(String(data.variantId), 1, { replace: true });
    } finally {
      this.#isUpdating = false;
      this.#setCardBusy(card, false);
      releaseGiftLock();
    }
  };

  /**
   * @param {{ variantId: string }} data
   * @param {Event} event
   */
  handleIncrement = async (data, event) => {
    if (!data?.variantId || this.#isUpdating) return;
    const entitlement = parseInt(this.dataset.entitlement || '0', 10);
    const pickedTotal = await this.#currentPickedTotal();
    if (pickedTotal >= entitlement) return;
    if (!acquireGiftLock()) return;
    this.#isUpdating = true;
    const card = event?.currentTarget instanceof Element ? event.currentTarget.closest('[data-variant-id]') : null;
    this.#setCardBusy(card, true);
    try {
      await this.#syncPick(String(data.variantId), 1, { delta: true });
    } finally {
      this.#isUpdating = false;
      this.#setCardBusy(card, false);
      releaseGiftLock();
    }
  };

  /**
   * @param {{ variantId: string }} data
   * @param {Event} event
   */
  handleDecrement = async (data, event) => {
    if (!data?.variantId || this.#isUpdating) return;
    if (!acquireGiftLock()) return;
    this.#isUpdating = true;
    const card = event?.currentTarget instanceof Element ? event.currentTarget.closest('[data-variant-id]') : null;
    this.#setCardBusy(card, true);
    try {
      await this.#syncPick(String(data.variantId), -1, { delta: true });
    } finally {
      this.#isUpdating = false;
      this.#setCardBusy(card, false);
      releaseGiftLock();
    }
  };

  /**
   * Toggles the visible spinner overlay on the card currently being
   * changed, so switching coffret picks (or stepping a quantity) doesn't
   * read as unresponsive during the network round trip. Purely
   * presentational — `#isUpdating`/the shared gift lock already own the
   * real re-entrancy guard. Safe to call with a stale/detached `card`
   * reference (e.g. one whose DOM node was already replaced by
   * cart-gift-picker.js's syncContent() before this ran): toggling a class
   * on a disconnected element is a harmless no-op.
   * @param {Element|null} card
   * @param {boolean} busy
   */
  #setCardBusy(card, busy) {
    if (!card) return;
    card.classList.toggle('is-loading', busy);
  }

  /**
   * Reads the live cart and sums _bogo_routines_gift quantities.
   * @returns {Promise<number>}
   */
  async #currentPickedTotal() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return cart.items
      .filter((item) => item.properties && item.properties['_bogo_routines_gift'] === 'true')
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Finds the existing cart line (if any) for a picked variant.
   * @param {string} variantId
   * @returns {Promise<{ line: object|null, cart: object }>}
   */
  async #findExistingLine(variantId) {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const line = cart.items.find(
      (item) =>
        String(item.variant_id) === variantId &&
        item.properties &&
        item.properties['_bogo_routines_gift'] === 'true',
    );
    return { line: line || null, cart };
  }

  /**
   * Adds, increments, decrements, or removes a picked variant's gift
   * line depending on mode. Caller owns acquiring/releasing the shared
   * gift lock and the #isUpdating guard.
   * @param {string} variantId
   * @param {number} amount - +1 to add/increment, -1 to decrement
   * @param {{ replace?: boolean, delta?: boolean }} mode
   */
  async #syncPick(variantId, amount, mode) {
    const { line } = await this.#findExistingLine(variantId);

    if (mode.replace) {
      // Single-pick mode: remove any other routines pick, then ensure
      // this one exists at quantity 1.
      await this.#removeAllOtherPicks(variantId);
      if (!line) {
        await this.#addGiftLine(variantId, 1);
      }
      return;
    }

    // Stepper mode.
    if (amount > 0) {
      if (line) {
        await this.#changeLineQuantity(line.key, line.quantity + 1);
      } else {
        await this.#addGiftLine(variantId, 1);
      }
    } else if (line) {
      const nextQty = line.quantity - 1;
      await this.#changeLineQuantity(line.key, Math.max(nextQty, 0));
    }
  }

  /**
   * Removes every _bogo_routines_gift line except the one matching
   * variantId (used by single-pick mode to swap the pick in one action).
   * @param {string} keepVariantId
   */
  async #removeAllOtherPicks(keepVariantId) {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const others = cart.items.filter(
      (item) =>
        item.properties &&
        item.properties['_bogo_routines_gift'] === 'true' &&
        String(item.variant_id) !== keepVariantId,
    );
    for (const item of others) {
      await this.#changeLineQuantity(item.key, 0);
    }
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  async #addGiftLine(variantId, quantity) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));
    formData.append('properties[_bogo_routines_gift]', 'true');
    formData.append('properties[_free_gift]', 'true');
    formData.append('sections', this.dataset.sectionId || '');
    await this.#post('/cart/add.js', formData);
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity
   */
  async #changeLineQuantity(lineKey, quantity) {
    const formData = new FormData();
    formData.append('id', lineKey);
    formData.append('quantity', String(quantity));
    formData.append('sections', this.dataset.sectionId || '');
    await this.#post('/cart/change.js', formData);
  }

  /**
   * @param {string} url
   * @param {FormData} formData
   */
  async #post(url, formData) {
    await postAndSync(url, formData, this.dataset.sectionId, this.id);
  }
}

if (!customElements.get('cart-bogo-routines-component')) {
  customElements.define('cart-bogo-routines-component', CartBogoRoutinesComponent);
}

/* ── Shared cross-promo mutex ──────────────────────────────────────────────
 * All BOGO-style watchers (routines, litres, and any future one) share one
 * owner-aware, TTL-bounded lock at window.__lvGiftLock so two of them can
 * never race an add/change against each other. Duplicated verbatim in
 * assets/cart-bogo-litres.js under its own LOCK_OWNER, matching
 * grid-2027's own "Cross-promo mutex" convention (each promo script
 * carries its own tiny copy rather than sharing an import) — see
 * .claude/rules/cart-promotions.md.
 */
const LOCK_OWNER = 'bogo-routines';
const LOCK_TTL_MS = 10000;

function acquireGiftLock() {
  const held = window.__lvGiftLock;
  if (held && Date.now() - held.at < LOCK_TTL_MS) return false;
  window.__lvGiftLock = { owner: LOCK_OWNER, at: Date.now() };
  return true;
}

function releaseGiftLock() {
  const held = window.__lvGiftLock;
  if (held && held.owner === LOCK_OWNER) window.__lvGiftLock = null;
}

/**
 * POSTs a cart mutation, morphs the affected section from the response's
 * own sections payload, and dispatches CartUpdateEvent — the same
 * dispatch-after-morph order cart-bogo-litres.js and the rest of this
 * theme's own cart-mutating components already use (component-cart-items.js
 * is the one exception that dispatches before its morph; deferring on the
 * LISTENING side, not here, is what assets/cart-gift-picker.js does to stay
 * correct regardless of which order a given caller uses).
 * @param {string} url
 * @param {FormData} formData
 * @param {string} [sectionId]
 * @param {string} [sourceId]
 */
async function postAndSync(url, formData, sectionId, sourceId) {
  const response = await fetch(url, fetchConfig('javascript', { body: formData }));
  if (!response.ok) throw new Error(`Cart BOGO routines request failed: ${url}`);

  const data = await response.json();
  if (data.sections && sectionId && data.sections[sectionId]) {
    await morphSection(sectionId, data.sections[sectionId]);
  }

  document.dispatchEvent(
    new CartUpdateEvent(data, sourceId || 'cart-bogo-routines-reconciler', {
      source: 'cart-bogo-routines',
      itemCount: data.item_count,
    }),
  );
}

/* ── Reconciler ──────────────────────────────────────────────────────────
 * The click handlers above only ever ask for a +/-1 change gated by a
 * snapshot of the cart taken at click time, so without a background
 * reconciler a customer could still end up over/under entitlement — e.g.
 * remove a bought coffret after picking gifts (their picks now exceed
 * what they're owed), or buy more of the trigger item (their picks now
 * fall short of what they're owed, and the selector would otherwise sit
 * there under-filled until they manually add more). This keeps the cart's
 * actual gift-line quantities in sync with entitlement on every cart
 * change — ported from grid-2027's own reconciler, same trim/top-up
 * rules, adapted to morph+dispatch (postAndSync) instead of
 * window.cartDrawer.refresh().
 *
 * Trim rule (picks > entitlement): reduce the MOST EXPENSIVE picked
 * coffret(s) first. The discount function already protects the merchant
 * regardless (it always zeroes the cheapest picked units first up to
 * entitlement, see cart-promo-discount), so trimming the priciest picks
 * out of the cart first keeps what's actually left in the cart matching
 * what the function would have discounted anyway, rather than showing the
 * customer a stale pricier "free" pick that the function silently didn't
 * discount.
 *
 * Top-up rule (picks < entitlement): default the shortfall onto whichever
 * trigger variant was most recently seen in the cart scan (the coffret the
 * customer just bought) — this is what lets a customer buy more without
 * ever having to reselect a coffret from scratch.
 *
 * Uses a fresh /cart.js fetch for its own entitlement math rather than
 * reading any DOM attribute (e.g. data-entitlement), so it's correct
 * regardless of whether the section's own morph has landed yet by the
 * time cart:update fires — see assets/cart-gift-picker.js's own comment
 * on Horizon's inconsistent dispatch-before-or-after-morph ordering.
 */
/* Wrapped in an initializer that waits for the config script via
 * MutationObserver rather than a single top-level document.querySelector
 * (or an earlier, capped-retry version of this same fix). Root cause,
 * confirmed live via direct diagnostic logging: <div data-gift-sections
 * hidden> in cart-drawer.liquid only renders inside the {% else %} branch
 * of {% if cart.empty? %} — on a fresh page load with an EMPTY cart, that
 * whole div (and this config script inside it) is genuinely absent from
 * the server-rendered HTML, full stop, not present "a little later" or
 * cached stale. It only starts existing once the drawer's own section is
 * morphed with a non-empty cart — i.e. the moment the customer's very
 * first add-to-cart action completes and re-renders the section. That
 * morph's own timing depends on network latency and whatever else is
 * happening on the page (this store loads a large number of third-party
 * scripts/pixels), so it is NOT bounded by any fixed timeout — a capped
 * retry (tried first: 5 attempts over 2 seconds) raced against it and
 * lost unpredictably, which is exactly what read as the default gift
 * "randomly" not appearing: on a fast morph the retry window happened to
 * be enough, on a slower one it gave up just before the config existed.
 * A MutationObserver has no such ceiling — it reacts the instant the
 * config script is actually inserted, however long that takes, and
 * disconnects itself once found. */
function initReconciler() {
  const configEl = document.querySelector('[data-bogo-routines-config]');

  if (!configEl) {
    const observer = new MutationObserver(() => {
      const el = document.querySelector('[data-bogo-routines-config]');
      if (el) {
        observer.disconnect();
        initReconciler();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return;
  }

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    config = null;
  }

  if (config && config.enabled) {
    const triggerVariantIds = {};
    (config.triggerVariantIds || []).forEach((id) => {
      triggerVariantIds[String(id)] = true;
    });

    if (Object.keys(triggerVariantIds).length > 0) {
      /* Retry-counted, not a permanent one-shot blacklist. A single 422
       * isn't necessarily "this variant is genuinely out of stock" — it
       * can also be a transient inventory-reservation race. Treating one
       * 422 as permanent for the rest of the page's session was a real
       * contributor to the default gift "randomly" never appearing: one
       * bad response and it would never retry again without a full page
       * reload. Now it retries up to MAX_UNAVAILABLE_ATTEMPTS times
       * before giving up, and a later success resets the counter. */
      const unavailableAttempts = {}; // variantId -> consecutive 422 count this session
      const MAX_UNAVAILABLE_ATTEMPTS = 3;
      let suppress = false;
      let isUpdating = false;
      let debounceTimer = null;
      let lastKnownBoughtQty = null; // null until the first reconcile pass has run

      const getSectionId = () => {
        const el = document.querySelector('[data-bogo-routines-config]');
        return el ? el.dataset.sectionId : configEl.dataset.sectionId;
      };

      function refresh() {
        suppress = true;
        setTimeout(() => {
          suppress = false;
        }, 800);
      }

      /* async + try/finally rather than a hand-chained .then/.catch/.then:
       * a chained version left a real, confirmed gap — if fetchConfig(...)
       * or any other expression between `isUpdating = true` and the first
       * `.then()` threw SYNCHRONOUSLY, the .catch()/.then() cleanup chain
       * was never attached at all, and isUpdating stayed stuck at `true`
       * forever. Since isUpdating is a single shared flag gating every
       * future reconcile action (top-up AND removal) for this promo, one
       * silent synchronous throw permanently wedged the whole reconciler —
       * matching a real live report where a stale, no-longer-entitled
       * gift line stopped being removed at all. try/finally guarantees the
       * cleanup runs regardless of where in the body something throws. */
      async function changeQty(item, qty) {
        if (isUpdating) return;
        isUpdating = true;
        try {
          const formData = new FormData();
          formData.append('id', item.key);
          formData.append('quantity', String(qty));
          formData.append('sections', getSectionId() || '');
          await postAndSync('/cart/change.js', formData, getSectionId());
          refresh();
        } catch (e) {
          console.error('[BOGO Routines] Reconcile failed:', e);
        } finally {
          isUpdating = false;
        }
      }

      /**
       * Only advances lastKnownBoughtQty on a CONFIRMED successful add —
       * see reconcile()'s own comment on why the caller must not advance
       * it itself when a top-up is attempted. Advancing it eagerly
       * (regardless of outcome) was the second half of the same stuck-
       * reconciler bug: a top-up that silently no-op'd (isUpdating already
       * true from an overlapping reconcile pass, or any other early
       * return) still left the caller marking that entitlement as "seen,"
       * permanently skipping the retry every future pass would otherwise
       * have attempted.
       * @param {string} variantId
       * @param {number} qty
       */
      async function addDefaultGift(variantId, qty) {
        if (isUpdating || (unavailableAttempts[variantId] || 0) >= MAX_UNAVAILABLE_ATTEMPTS) return;
        if (!acquireGiftLock()) return;
        isUpdating = true;
        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', String(qty));
          formData.append('properties[_bogo_routines_gift]', 'true');
          formData.append('properties[_free_gift]', 'true');
          formData.append('sections', getSectionId() || '');

          const res = await fetch('/cart/add.js', fetchConfig('javascript', { body: formData }));
          if (res.status === 422) {
            unavailableAttempts[variantId] = (unavailableAttempts[variantId] || 0) + 1;
            return;
          }
          if (!res.ok) throw new Error(String(res.status));

          const data = await res.json();
          const sectionId = getSectionId();
          if (data.sections && sectionId && data.sections[sectionId]) {
            await morphSection(sectionId, data.sections[sectionId]);
            document.dispatchEvent(
              new CartUpdateEvent(data, 'cart-bogo-routines-reconciler', {
                source: 'cart-bogo-routines',
                itemCount: data.item_count,
              }),
            );
          }
          refresh();
          lastKnownBoughtQty = (lastKnownBoughtQty || 0) + qty;
          unavailableAttempts[variantId] = 0;
        } catch (e) {
          console.error('[BOGO Routines] Default add failed:', e);
        } finally {
          isUpdating = false;
          releaseGiftLock();
        }
      }

      function reconcile() {
        if (suppress || isUpdating) return;

        fetch('/cart.js')
          .then((res) => (res.ok ? res.json() : null))
          .then((cart) => {
            if (!cart) return;

            let boughtQty = 0;
            let lastTriggerVariantId = null;
            const giftItems = [];

            (cart.items || []).forEach((item) => {
              const p = item.properties || {};
              if (p._bogo_routines_gift === 'true') {
                giftItems.push(item);
                return;
              }
              // A gift line never earns another gift.
              if (p._free_gift === 'true' || p._tier_gift === 'true') return;
              if (triggerVariantIds[String(item.variant_id)]) {
                boughtQty += item.quantity;
                lastTriggerVariantId = String(item.variant_id);
              }
            });

            let pickedTotal = 0;
            giftItems.forEach((item) => {
              pickedTotal += item.quantity;
            });

            if (boughtQty === 0) {
              // Trigger no longer met at all — drop every pick.
              lastKnownBoughtQty = 0;
              const overage = giftItems.find((item) => item.quantity > 0);
              if (overage) changeQty(overage, 0);
              return;
            }

            if (pickedTotal > boughtQty) {
              // Trim the priciest pick(s) down to fit, one step at a
              // time — each trim triggers its own refresh, and the next
              // reconcile pass continues trimming if more than one unit
              // needs to go. Deliberately does NOT touch
              // lastKnownBoughtQty here — entitlement itself hasn't
              // changed.
              const priciest = giftItems.reduce(
                (max, item) => (!max || item.price > max.price ? item : max),
                null,
              );
              if (priciest) changeQty(priciest, priciest.quantity - 1);
              return;
            }

            // Top up ONLY the entitlement that's newly appeared since the
            // last pass — never the full gap between picks and
            // entitlement. Using the full gap was a real, confirmed bug in
            // grid-2027: a customer who bought a coffret, got it
            // auto-picked, then tried to swap to a different one by
            // decrementing it, was fighting this reconciler the whole
            // time — every decrement drops pickedTotal below boughtQty for
            // a moment, and the old "top up to boughtQty" rule immediately
            // re-added the same default coffret before the customer could
            // ever increment a different one. Tracking what entitlement
            // this reconciler has already SEEN (lastKnownBoughtQty) and
            // only filling the delta means a deliberate decrement is
            // respected and stays empty (freeing real budget for a
            // different pick), while genuinely buying more still
            // auto-fills without requiring a reselect.
            if (lastKnownBoughtQty === null) lastKnownBoughtQty = 0;
            const newEntitlement = boughtQty - lastKnownBoughtQty;
            if (newEntitlement > 0 && pickedTotal < boughtQty && lastTriggerVariantId) {
              const shortfall = boughtQty - pickedTotal;
              const topUp = Math.min(newEntitlement, shortfall);
              if (topUp > 0) {
                // addDefaultGift owns advancing lastKnownBoughtQty itself,
                // and ONLY on confirmed success — see its own comment.
                // Do NOT also advance it here: a top-up that silently
                // no-ops (isUpdating already true, a stale gift lock, a
                // dropped promise) would otherwise get marked "seen"
                // regardless, permanently skipping every future retry.
                addDefaultGift(lastTriggerVariantId, topUp);
                return;
              }
            }
            // Nothing pending to top up (already fully caught up, or no
            // new entitlement since last pass) — safe to sync outright.
            lastKnownBoughtQty = boughtQty;
          })
          .catch((e) => console.error('[BOGO Routines]', e));
      }

      document.addEventListener('cart:update', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(reconcile, 200);
      });

      setTimeout(reconcile, 600);

      /* Periodic safety net, in addition to the event-driven passes above.
       * The event-driven path depends on a well-formed cart:update firing
       * at all and on none of the several other concurrent cart watchers
       * in this theme (each doing their own /cart.js fetch + mutation)
       * racing this one out of a single attempt — confirmed live to
       * occasionally not result in the default gift landing on the very
       * first try, reading as "random" flakiness. reconcile() is cheap
       * (one GET, no-ops when suppress/isUpdating or when already caught
       * up) and idempotent, so polling it every few seconds for as long
       * as the page is open costs little and makes the default-gift
       * outcome self-correcting within a few seconds instead of depending
       * on any single event actually triggering the fix. */
      setInterval(reconcile, 3000);
    }
  }
}

try {
  initReconciler();
} catch (e) {
  console.error('[BOGO Routines] initReconciler threw:', e);
}
